import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { useCart, useAuth } from '../context/AppContext';
import { Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function CartSidebar({ isOpen, onClose }) {
  const { cart, removeFromCart, loading } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleRemove = async (experienceId) => {
    try {
      await removeFromCart(experienceId);
      toast.success('Borttagen från varukorgen');
    } catch (err) {
      toast.error('Kunde inte ta bort');
    }
  };

  const handleCheckout = () => {
    onClose();
    navigate('/kassa');
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-md bg-[#F9F9F7]" data-testid="cart-sidebar">
        <SheetHeader>
          <SheetTitle className="font-['Avenir_Next'] text-2xl flex items-center gap-2">
            <ShoppingBag className="w-6 h-6" />
            Varukorg
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col h-[calc(100vh-8rem)] mt-6">
          {!user ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
              <ShoppingBag className="w-16 h-16 text-[#8C8C8C] mb-4" />
              <p className="text-[#8C8C8C]">Logga in för att se din varukorg</p>
            </div>
          ) : cart.items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
              <ShoppingBag className="w-16 h-16 text-[#8C8C8C] mb-4" />
              <p className="text-[#8C8C8C]">Din varukorg är tom</p>
              <Button
                variant="ghost"
                className="mt-4 text-[#8B6F4E]"
                onClick={() => { onClose(); navigate('/upplevelser'); }}
              >
                Utforska upplevelser
              </Button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                {cart.items.map((item) => (
                  <div
                    key={item.experience_id}
                    className="flex gap-4 p-4 bg-white rounded-xl"
                    data-testid={`cart-item-${item.experience_id}`}
                  >
                    <img
                      src={item.experience?.image_url}
                      alt={item.experience?.name}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-[#1A1A1A] truncate">
                        {item.experience?.name}
                      </h4>
                      <p className="text-sm text-[#8C8C8C]">
                        {item.experience?.location} • {item.experience?.duration}
                      </p>
                      <p className="text-[#8B6F4E] font-semibold mt-1">
                        {item.experience?.price} kr
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemove(item.experience_id)}
                      className="p-2 text-[#B83A3A] hover:bg-[#B83A3A]/10 rounded-lg transition-colors"
                      data-testid={`remove-cart-item-${item.experience_id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#2C3E36]/10 pt-4 mt-4 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-[#8C8C8C]">Totalt</span>
                  <span className="text-2xl font-['Avenir_Next'] font-medium text-[#8B6F4E]">
                    {cart.total.toLocaleString('sv-SE')} kr
                  </span>
                </div>
                <Button
                  onClick={handleCheckout}
                  className="w-full btn-primary h-14 text-base"
                  data-testid="checkout-button"
                >
                  Till kassan
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-center text-xs text-[#8C8C8C]">
                  <span className="klarna-badge">Klarna</span>
                  <span className="ml-2">Betala nu eller senare</span>
                </p>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
